import React, { useEffect, useState } from 'react';
import { CSVLink } from 'react-csv';
import ExcelJS from 'exceljs';
import { useAppDispatch } from '../../../../app/hooks';
import { tryToFetchAssetValues } from '../../../../store/brokers/admin/assets/assetsSlice';
import { tryToDownloadPurchaseSamples } from '../../../../store/brokers/partners/purchase-orders/purchaseOrdersSlice';
import CatalogFiltersSample from '../details/CatalogFiltersSample';
import SelectCheckbox from '../../../../shared/select-checkbox';
import Loading from '../../../../shared/Loading';
import Button from '../../../../shared/button';
import Modal from '../../../../shared/modal';
import Error from '../../../../shared/error';
import { tryToFetchAssetTemplateFieldsFilter } from '../../../../store/brokers/admin/asset-templates/assetsTemplatesSlice';


interface IDownloadSampleModal {
    openDownloadModal: boolean;
    sectors: Array<any>;
    handleCloseDownloadModal: () => void;
}

const DownloadSampleModal = ({
    openDownloadModal,
    sectors,
    handleCloseDownloadModal
}: IDownloadSampleModal) => {
    const dispatch = useAppDispatch();
    const csvLink = React.useRef() as React.MutableRefObject<any>;
    const [selectedSector, setSelectedSector] = useState<any>()
    const [downloadError, setDownloadError] = useState<string>()
    const [templateFields, setTemplateFields] = useState<any>()
    const [inventoryFields, setInventoryFields] = useState<any>();
    const [extendedFields, setExtendedFields] = useState<any>();
    const [categoryFields, setCategoryFields] = useState<any>();
    const [loadingDownload, setLoadingDownload] = useState<boolean>(false)
    const [templateData, setTemplateData] = useState<Array<any>>()
    const [filterValues, setFilterValues] = useState<any>()
    const [loadingFields, setLoadingFields] = useState<boolean>(false)
    const [loadingItemFields, setLoadingItemFields] = useState<boolean>(false)
    const [searchField, setSearchField] = useState<any>()
    const [filteredFields, setFilteredFields] = useState<any>()
    const [loadingOptions, setLoadingOptions] = useState<any>()

    const onChangeSelectedSector = (e: any) => {
        setSelectedSector(e)
    }

    const onCloseModal = () => {
        handleCloseDownloadModal();
        setSelectedSector(undefined)
        setDownloadError(undefined)
        setLoadingDownload(false)
        setFilterValues(undefined)
        setLoadingFields(false)
        setLoadingItemFields(false)
        setCategoryFields(undefined)
        setInventoryFields(undefined)
        setExtendedFields(undefined)
    }

    const onGettingAllAssetFields = async () => {
        if (selectedSector) {
            try {
                const response: any = await dispatch(tryToFetchAssetTemplateFieldsFilter(selectedSector?.value)).unwrap();
                if (response?.fields) {
                    setCategoryFields(response?.fields);
                    setExtendedFields(response?.extendedFields)
                    setInventoryFields(response?.inventoryFields);
                }
            } catch (err) {
                // error here
            }
        }
    };

    const onGettingFieldValues = async () => {
        if (selectedSector) {
            let data: any = {
                exclude: true,
                values: []
            }
            if (filterValues) {
                const keysArray = filterValues && Object.keys(filterValues)
                const formatValues = keysArray && keysArray?.length > 0 && keysArray?.map((name: string) => { return { [name]: filterValues?.[name] } })
                data = {
                    ...data || {},
                    values: formatValues || []
                }
            }
            if (searchField) {
                data = {
                    ...data || {},
                    search: {
                        ...searchField || {}
                    }
                }
            }
            const response = await dispatch(tryToFetchAssetValues({ id: selectedSector?.id, data })).unwrap()
            setTemplateFields(response || [])
            setLoadingFields(false)
            setLoadingItemFields(false)
            setLoadingOptions(undefined)
        }
    }

    useEffect(() => {
        if (selectedSector) {
            onGettingFieldValues()
            onGettingAllAssetFields()
            setLoadingFields(true)
        }
    }, [selectedSector])

    useEffect(() => {
        if (selectedSector) {
            onGettingFieldValues()
        }
    }, [filterValues, searchField])

    const columnNumberToLetter = (colNum: number) => {
        let letter = '';
        while (colNum > 0) {
            const remainder = (colNum - 1) % 26;
            letter = String.fromCharCode(65 + remainder) + letter;
            colNum = Math.floor((colNum - 1) / 26);
        }
        return letter;
    };

    const processData = (data: any, isExtended = false) => {
        const requiredFields: any = [];
        const optionalFields: any = [];
        let hasColor = false;

        data?.forEach((item: any) => {
            if (isExtended && item?.name?.toLowerCase() === 'color') {
                hasColor = true;
                return;
            }

            const formattedName = item.name
                .split('_')
                .map((word: any) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ');

            if (item?.required) {
                requiredFields.push(formattedName);
            } else {
                optionalFields.push(formattedName);
            }
        });

        return { requiredFields, optionalFields, hasColor };
    };

    const formatString = (item: string) => {
        return item
            .replace(/_/g, ' ')
            .toLowerCase()
            .split(' ')
            .map((word: any) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }

    const downloadExcel = async (data?: any) => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Styled Sheet');
        worksheet.getRow(1).height = 100;
        worksheet.getCell('A1').value = 'wingpro';
        worksheet.getCell('A1').fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'F7931E' },
        };
        worksheet.getCell('A1').font = { size: 30, bold: true, name: 'Docs-Figtree' };
        worksheet.getCell('A1').alignment = { vertical: 'middle', horizontal: 'left' };

        const columnCount = data?.[0]?.length > 0 ? data?.[0]?.length : 2
        const endColumnLetter = columnNumberToLetter(columnCount)
        worksheet.mergeCells(`B1:${endColumnLetter}1`);
        const mergedCell = worksheet.getCell('B1');
        mergedCell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'F7931E' },
        };
        const result1 = processData(categoryFields);
        const result2 = processData(inventoryFields);
        const result3 = processData(extendedFields, true);

        const allRequiredFields = [...result1.requiredFields, ...result2.requiredFields, ...result3.requiredFields];
        const allOptionalFields = [...result1.optionalFields, ...result2.optionalFields, ...result3.optionalFields];
        const hasColorInData2 = result3?.hasColor;
        let formRequiredText: any;
        let formOptionalText: any;
        let colorText: any;
        if (allRequiredFields && allRequiredFields?.length > 0) {
            const fieldsJoin = allRequiredFields?.join(', ');
            formRequiredText = [
                { text: `${fieldsJoin} `, font: { bold: true, name: 'Figtree' } },
                { text: `(Required): Enter the item's details in ${allRequiredFields?.length === 1 ? 'this field' : 'these fields'}.\n`, font: { bold: false, name: 'Figtree' } },
            ]
        }
        if (allOptionalFields && allOptionalFields?.length > 0) {
            const fieldsJoin = allOptionalFields?.join(', ');
            formOptionalText = [
                { text: `${fieldsJoin} `, font: { bold: true, name: 'Figtree' } },
                { text: `(Optional): You can leave ${allOptionalFields?.length === 1 ? 'this field' : 'these fields'} blank.\n`, font: { bold: false, name: 'Figtree' } },
            ]
        }
        if (hasColorInData2) {
            colorText = [
                { text: 'Color ', font: { bold: true, name: 'Figtree' } },
                { text: '(Optional): If you don\'t need a specific color, you can leave this field blank. We\'ll assume \'any color\' for that item.\n', font: { bold: false, name: 'Figtree' } },
            ]
        }
        mergedCell.value = {
            richText: [
                ...formRequiredText || [],
                ...formOptionalText || [],
                ...colorText || [],
                { text: 'Quantity ', font: { bold: true, name: 'Figtree' } },
                { text: '(Required): Enter the number of items. If left blank, we\'ll automatically set the quantity to 1.', font: { bold: false, name: 'Figtree' } },
                { text: '\nPrice ', font: { bold: true, name: 'Figtree' } },
                { text: '(Optional): You can fill this in if you want to set a price per item. If left blank, you\'ll have the option to set a total price for all the items on the platform later.', font: { bold: false, name: 'Figtree' } },
            ]
        }
        mergedCell.font = { size: 10 };
        mergedCell.alignment = { wrapText: true, vertical: 'middle', horizontal: 'right' };

        for (let i = 1; i <= columnCount; i++) {
            worksheet.getColumn(i).width = i === 1 ? 30 : 13;
            const cell = worksheet.getCell(`${columnNumberToLetter(i)}2`);
            cell.value = data?.[0]?.[i - 1] ? formatString(data?.[0]?.[i - 1]) : data?.[0]?.[i - 1];
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: '202020' },
            };
            cell.border = {
                left: { style: 'thin', color: { argb: 'FFFFFFFF' } }
            };
            cell.alignment = {
                vertical: 'middle',
                horizontal: 'center'
            };
            cell.font = {
                color: { argb: 'FFFFFF' },
                name: 'Avenir',
                bold: true,
                size: 12
            };
        }

        data?.slice(1).forEach((row: any, rowIndex: number) => {
            row?.forEach((cellValue: any, colIndex: number) => {
                const cell = worksheet.getCell(`${columnNumberToLetter(colIndex + 1)}${rowIndex + 3}`);
                cell.value = cellValue;
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
            });
        });

        workbook.xlsx.writeBuffer().then((buffer: any) => {
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Purchase_order_${selectedSector?.label || ''}_template.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        });
        onCloseModal()
        setLoadingDownload(false)
    };

    const onDownloadTemplate = async (excel?: boolean) => {
        if (loadingDownload) {
            return;
        }
        setLoadingDownload(true)
        if (!selectedSector) {
            setDownloadError('Please select a category first to continue with download.')
            setLoadingDownload(false)
            return;
        }
        try {
            const filters = filterValues && Object.keys(filterValues)
            let formatFilters
            const filtersValuesFormat = filters && filters?.length > 0 && filters?.map((item: any) => {
                return {
                    name: item,
                    value: Object.keys(filterValues) ? filterValues?.[item] || undefined : undefined
                }
            })
            if (filtersValuesFormat && filtersValuesFormat?.length > 0) {
                formatFilters = {
                    fields: filtersValuesFormat?.filter((key: any) => key?.value)
                }
            }
            const response: any = await dispatch(tryToDownloadPurchaseSamples({ id: selectedSector?.id || '', data: formatFilters })).unwrap()
            if (excel) {
                downloadExcel(response || [])
            } else {
                setTemplateData(response || [])
            }
            setDownloadError(undefined)
        } catch (err: any) {
            setDownloadError(`${err}`)
            setLoadingDownload(false)
        }
    }

    useEffect(() => {
        if (templateData && loadingDownload) {
            csvLink.current.link.click()
            onCloseModal()
        }
        setLoadingDownload(false)
    }, [templateData])

    const onClearAllFilters = () => {
        setFilterValues(undefined)
    }

    const onChangeSelectedFilterValue = (value: any, type?: string) => {
        if (type) {
            setLoadingItemFields(true)
            setFilterValues({
                ...(filterValues || {}),
                [type]: value?.value || undefined,
            });
            if (searchField?.[type]) {
                setSearchField(undefined)
            }
        }
    };

    const onMenuCloseAfterSearch = (type?: string) => {
        if (type && searchField?.[type]) {
            setSearchField(undefined)
        }
    }

    const onChangeSearchAssetFields = (e: any, type?: string) => {
        if (type && e) {
            if (templateFields && templateFields?.[type]) {
                const formatFilteredFields = templateFields?.[type] && templateFields?.[type]?.length > 0 && templateFields?.[type]?.filter((next: any) => (next || '').toLowerCase().includes((e || '')?.toLowerCase()))
                if (formatFilteredFields && formatFilteredFields?.length > 0) {
                    setFilteredFields({ [type]: formatFilteredFields })
                }
                else {
                    setFilteredFields(undefined)
                    setLoadingOptions({ [type]: true })
                    setSearchField({
                        [type]: e
                    })
                    if (filterValues?.[type]) {
                        const formatFormData = Object.assign(filterValues)
                        delete formatFormData[type]
                        setFilterValues(
                            {
                                ...formatFormData || {}
                            }
                        )
                    }
                }
            }
        } else {
            setFilteredFields(undefined)
        }
    }

    const onClearAllFields = () => {
        setFilterValues(undefined)
        setSearchField(undefined)
        setFilteredFields(undefined)
        setLoadingItemFields(false)
    }

    return (
        <Modal
            open={openDownloadModal}
            onClose={onCloseModal}
            showInRight={true}
            contentContainerStyle={'!min-w-[650px]'}>
            <div className={'p-2 min-w-[600px]'}>
                <div className='border-b-2 border-slate-200 pb-2 my-1'>
                    <p className='mb-2 font-semibold text-sm'>Download Template</p>
                </div>
                <div className='p-2 !rounded bg-[#f8f9fc]'>
                    <p className='mb-4 font-bold'>Select a category to download CSV or Excel template.</p>
                    {downloadError && <Error text={downloadError} />}
                    <div className='flex flex-col items-center'>
                        <SelectCheckbox
                            name='Category'
                            placeholder=' '
                            dataQa={selectedSector?.name === undefined ? 'category-selected' : selectedSector?.name.toLowerCase().replaceAll(' ', '-')}
                            containerStyle='mb-1 min-w-full'
                            selectedOption={selectedSector}
                            onChangeSelectedOption={onChangeSelectedSector}
                            options={sectors || []}
                        />
                        {loadingFields ?
                            <div className='flex flex-rows col-span-4 justify-center items-center my-7'>
                                <Loading />
                            </div>
                            :
                            <div className={'min-w-[100%]'}>
                                {(selectedSector && templateFields) &&
                                    <>
                                        <p className='mt-4 mb-2'>Select the filters you want to apply to the selected template, or download the full catalog.</p>
                                        <CatalogFiltersSample
                                            templateFields={templateFields || []}
                                            filterValues={filterValues || null}
                                            loadingFields={loadingItemFields}
                                            loadingOptions={loadingOptions}
                                            fieldValues={templateFields}
                                            filteredFields={filteredFields}
                                            onMenuCloseAfterSearch={onMenuCloseAfterSearch}
                                            onChangeSelectedValue={onChangeSelectedFilterValue}
                                            onChangeSearchAssetFields={onChangeSearchAssetFields}
                                            onClearAllFilters={onClearAllFilters}
                                        />
                                    </>
                                }
                            </div>
                        }
                        <div>
                            {(selectedSector && templateFields) &&
                                <Button
                                    label={'Clear Fields'}
                                    onClickButton={() => onClearAllFields()}
                                    disabled={loadingDownload}
                                    className={`${loadingDownload ? 'btn-main-disable' : 'btn-main'} my-3 !py-1 !rounded !shadow-none mr-3`}
                                />
                            }
                        </div>
                        <div className='flex flex-row justify-center'>
                            <Button
                                label={loadingDownload ? 'Loading data...' : 'Download Template CSV'}
                                onClickButton={() => !loadingDownload && onDownloadTemplate()}
                                disabled={loadingDownload}
                                dataQa={loadingDownload ? 'loading-data...' : 'download-catalog'}
                                className={`${loadingDownload ? 'btn-main-disable' : 'btn-primary'} !py-1 mr-3 !rounded !shadow-none`}
                            />
                            <Button
                                label={loadingDownload ? 'Loading data...' : 'Download Template Excel'}
                                className={`${loadingDownload ? 'btn-main-disable' : 'btn-main'} !rounded`}
                                onClickButton={() => onDownloadTemplate(true)}
                            />
                        </div>
                        <CSVLink
                            ref={csvLink}
                            data={templateData && templateData?.length > 0 ? templateData?.filter((item, idx) => idx > 0) : []}
                            headers={templateData?.[0] || []}
                            className={'none'}
                            target={'_blank'}
                            filename={`Purchase-order-${selectedSector?.name || 'catalog'}-template.csv`}
                        >
                        </CSVLink>
                    </div>
                </div>
            </div>
        </Modal >
    )
}
export default DownloadSampleModal;